/* eslint-disable react-refresh/only-export-components */
import { useConvexAuth, useQuery } from 'convex/react'
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'

import { authClient } from '@/lib/auth/client'
import { clearStoredSession, hasStoredSession } from '@/lib/auth/session-storage'
import { api } from '@/lib/convex-api'
import { useHydrated } from '@/lib/use-hydrated'

// A single, normalized view of the viewer's auth + workspace state, derived from
// the backend (the source of truth) plus the local session token. Every route
// guard reads this instead of juggling useSession / useConvexAuth / per-page
// queries, which is what used to cause the login-page flash and blank screens.
//
//   loading         — still confirming; render a loader, never the wrong page
//   unauthenticated — no valid session; allowed on auth + public pages only
//   needs-workspace — signed in but no workspace yet; belongs on /onboarding
//   authenticated   — signed in with an active workspace; full app access
export type AuthStatus = 'disabled' | 'loading' | 'unauthenticated' | 'needs-workspace' | 'authenticated'

type AuthContextValue = {
  status: AuthStatus
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Value used during SSG/prerender and any client render that happens before the
// Convex provider is mounted. Resolving to 'loading' keeps the prerendered HTML
// identical to the first client render (a loader), so there's no hydration
// mismatch on the prerendered protected routes (/dashboard, /onboarding).
const LOADING_VALUE: AuthContextValue = { status: 'loading' }

export function StaticAuthProvider({ children }: { children: ReactNode }) {
  return <AuthContext.Provider value={LOADING_VALUE}>{children}</AuthContext.Provider>
}

export function AuthStateProvider({ children }: { children: ReactNode }) {
  // Gate everything behind hydration so the first client render matches the
  // server-rendered loader exactly (see LOADING_VALUE).
  const hydrated = useHydrated()
  // Better Auth owns the session: sign-in / sign-out write it directly and it
  // updates synchronously, so it's the authoritative "is there a session" signal.
  // useConvexAuth lags it (it has to fetch a Convex JWT from the new session),
  // which is exactly why a fresh login used to bounce — we'd read Convex as
  // not-authenticated for a beat and treat the brand-new session as expired.
  const { data: sessionData, isPending: sessionPending } = authClient.useSession()
  const betterAuthHasSession = Boolean(sessionData?.session)
  const { isAuthenticated, isLoading } = useConvexAuth()
  const storedSession = hasStoredSession()

  // Only ask the backend once Convex has validated the token, so the query
  // always runs authenticated. We subscribe to the SAME viewerRouteState the app
  // shell uses (Convex dedupes it into one subscription) and only read its
  // `status` — so this never depends on a not-yet-deployed backend function.
  const shouldCheckBackend = hydrated && isAuthenticated
  const backendStatus = useQuery(api.workspace.viewerRouteState, shouldCheckBackend ? {} : 'skip')

  // The backend reports the session is gone even though the cached Convex JWT
  // still validates (isAuthenticated stays true until the JWT expires/refreshes).
  // This is a server-side revocation — safeGetAuthUser re-checks the session row
  // + expiresAt on every query, so the backend is authoritative here.
  const backendSignedOut = shouldCheckBackend && backendStatus?.status === 'signed-out'

  const status = useMemo<AuthStatus>(() => {
    // Before hydration, stay on the loader to match the prerendered HTML.
    if (!hydrated) {
      return 'loading'
    }

    // No token in the browser ⇒ no session is possible. Resolve immediately so
    // the login form shows without a flash and protected routes redirect fast.
    if (!storedSession) {
      return 'unauthenticated'
    }

    // Trust Better Auth for whether a session exists. Wait while it resolves,
    // then honor it. This is what makes a fresh login NOT bounce: right after
    // sign-in Better Auth reports the session immediately, so we hold on the
    // loader (below) while Convex catches up instead of flashing 'unauthenticated'.
    if (sessionPending) {
      return 'loading'
    }

    if (!betterAuthHasSession) {
      return 'unauthenticated'
    }

    // Signed in per Better Auth; wait for Convex to be ready to run queries
    // (it fetches a JWT from the session — this is the lag we must not misread).
    if (isLoading || !isAuthenticated) {
      return 'loading'
    }

    // Authenticated at the Convex layer; the backend tells us where to route.
    if (backendStatus === undefined) {
      return 'loading'
    }

    if (backendStatus.status === 'ready') {
      return 'authenticated'
    }

    if (backendStatus.status === 'disabled') return 'disabled'

    if (backendStatus.status === 'needs-workspace') {
      return 'needs-workspace'
    }

    // backendStatus is 'signed-out' even though the Convex JWT still validates:
    // the session was revoked server-side. The backend is the source of truth,
    // so treat the viewer as signed out — the cleanup effect below invalidates
    // the stale token and the guard routes them to /login.
    return 'unauthenticated'
  }, [hydrated, storedSession, sessionPending, betterAuthHasSession, isLoading, isAuthenticated, backendStatus])

  // Auto-logout on a genuine server-side revocation: the backend says the
  // session is gone while the cached Convex JWT still validates, so neither
  // Better Auth nor Convex has noticed yet. Force a sign-out to invalidate the
  // stale token and route the viewer to /login. A normal expiry/logout needs no
  // help here — Better Auth clears its own session (and the stored cookie), which
  // the derivation above already reads as 'unauthenticated'. Ref-guarded so it
  // fires once per revocation, and reset when the session is healthy again.
  const didForceSignOut = useRef(false)

  useEffect(() => {
    if (!backendSignedOut) {
      didForceSignOut.current = false
      return
    }

    if (didForceSignOut.current) {
      return
    }

    didForceSignOut.current = true
    void authClient
      .signOut()
      .catch(() => {})
      .finally(() => clearStoredSession())
  }, [backendSignedOut])

  const value = useMemo<AuthContextValue>(() => ({ status }), [status])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    // Outside any provider (shouldn't happen in app routes) — treat as still
    // confirming rather than throwing, so nothing renders the wrong page.
    return LOADING_VALUE
  }

  return context
}
